import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import moment from 'moment-timezone';
import { useDateFormat } from '@/hooks';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import './ScheduleSettingsStyles.css';
import { useScheduleSettings } from './useScheduleSettings';
import {
  DayOfWeekOption,
  ScheduleSettingsTabsProps,
  TimeZoneValue,
} from './types';
import {
  MonthlySelector,
  TimeSettings,
  WeeklySelector,
  ScheduleDisplay,
} from '.';
import { getDefaultTimeZone } from '@/components/formBuilder/formFields/DateField/helpers';

export const ScheduleSettingsTabs = ({
  selectedTab,
  formRef,
  modalContainerRef,
  selectedReport,
}: ScheduleSettingsTabsProps) => {
  const currentUser = useSelector(selectCurrUser);
  const { time_zone } = currentUser || {};
  const { dateFormat, timeFormat } = useDateFormat();
  const { deliver_time, deliver_day_of_week, deliver_day_of_month, timezone } =
    formRef?.current?.values || {};

  const { getCurrentTimeInTimezone, formatDayWithOrdinal, handleDateChange } =
    useScheduleSettings({
      timeZone: time_zone,
      formRef,
      dateFormat,
    });

  const ScheduledTime = selectedReport?.deliver_time
    ? moment(selectedReport.deliver_time, 'HH:mm').format(timeFormat)
    : '';
  const { fullName: tzName, abbreviation: tzAbbr } =
    TIME_ZONE_CONSTANTS[selectedReport?.timezone] || {};
  const formattedScheduledDate = `${ScheduledTime} ${tzAbbr || ''} ${
    tzName ? `(${tzName})` : ''
  }`;

  const setSelectedTime = (time: string) => {
    formRef.current.setValues({
      ...formRef.current.values,
      deliver_time: time,
    });
  };

  const setSelectedDayOfWeek = (dayOfWeek: number) => {
    formRef.current.setValues({
      ...formRef.current.values,
      deliver_day_of_week: dayOfWeek,
    });
  };

  const setSelectedTimezone = (tz: TimeZoneValue) => {
    formRef.current.setValues({
      ...formRef.current.values,
      timezone: tz.key,
    });
    getCurrentTimeInTimezone(tz);
  };

  const dayOfWeekOptions: DayOfWeekOption[] = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' },
  ];

  useEffect(() => {
    if (!formRef?.current) return;

    // Always set frequency to selectedTab to ensure it's updated correctly
    const formValues: Record<string, any> = { frequency: selectedTab };
    // Get existing values or default to an empty object
    const currentValues = formRef.current.values || {};

    if (timezone) formValues.timezone = timezone;
    if (deliver_time) formValues.deliver_time = deliver_time;
    if (selectedTab === 'monthly')
      formValues.deliver_day_of_month = deliver_day_of_month || 1;
    if (selectedTab === 'weekly')
      formValues.deliver_day_of_week = deliver_day_of_week || 1;
    // Only update if we have values to update
    formRef.current.setValues({ ...currentValues, ...formValues });
  }, [
    timezone,
    deliver_time,
    deliver_day_of_month,
    deliver_day_of_week,
    selectedTab,
    formRef,
  ]);

  const renderScheduledDetail = () => {
    const formValues = formRef?.current?.values || {};
    const {
      frequency,
      timezone,
      deliver_time,
      deliver_day_of_week,
      deliver_day_of_month,
    } = formValues;

    // Check if there are any changes compared to the existing report
    const hasBasicChanges =
      frequency !== selectedReport?.frequency ||
      timezone !== selectedReport?.timezone ||
      deliver_time !== selectedReport?.deliver_time;

    const hasFrequencySpecificChanges =
      (frequency === 'weekly' &&
        deliver_day_of_week !== selectedReport?.deliver_day_of_week) ||
      (frequency === 'monthly' &&
        deliver_day_of_month !== selectedReport?.deliver_day_of_month);

    const hasChanges = hasBasicChanges || hasFrequencySpecificChanges;
    const getFormattedSchedule = (isNewSchedule, freq, day, timeString) => {
      const prefix = isNewSchedule
        ? 'The report will be sent'
        : 'The report is scheduled';

      let dayInfo = '';
      if (freq === 'weekly' && day) {
        const dayLabel =
          dayOfWeekOptions.find((option) => option.value === day)?.label || '';
        dayInfo = ` on ${dayLabel}`;
      } else if (freq === 'monthly' && day) {
        dayInfo = ` on the ${formatDayWithOrdinal(day)}`;
      }

      return `${prefix} ${freq}${dayInfo} at ${timeString}`;
    };

    // Case 1: There are changes to the schedule
    if (hasChanges && frequency && timezone && deliver_time) {
      const { fullName: tzName, abbreviation: tzAbbr } =
        TIME_ZONE_CONSTANTS[timezone] || {};
      const timeOnly = moment(deliver_time, 'HH:mm').format(timeFormat);
      const formattedTime = `${timeOnly} ${tzAbbr || ''} ${
        tzName ? `(${tzName})` : ''
      }`;

      const day =
        frequency === 'weekly' ? deliver_day_of_week : deliver_day_of_month;
      return getFormattedSchedule(true, frequency, day, formattedTime);
    }

    // Case 2: Display existing schedule
    else if (selectedReport?.frequency) {
      const day =
        selectedReport.frequency === 'weekly'
          ? selectedReport.deliver_day_of_week
          : selectedReport.deliver_day_of_month;

      return getFormattedSchedule(
        false,
        selectedReport.frequency,
        day,
        formattedScheduledDate
      );
    }

    return '';
  };

  return (
    <>
      {selectedTab === 'weekly' && (
        <WeeklySelector
          dayOfWeekOptions={dayOfWeekOptions}
          selectedDayOfWeek={deliver_day_of_week || 1}
          setSelectedDayOfWeek={setSelectedDayOfWeek}
        />
      )}

      {selectedTab === 'monthly' && (
        <MonthlySelector
          selectedDayOfMonth={deliver_day_of_month || 1}
          formatDayWithOrdinal={formatDayWithOrdinal}
          handleDateChange={handleDateChange}
          modalContainerRef={modalContainerRef}
          formRef={formRef}
        />
      )}

      {['daily', 'weekly', 'monthly'].includes(selectedTab) && (
        <TimeSettings
          selectedTime={
            deliver_time ||
            getCurrentTimeInTimezone(getDefaultTimeZone(time_zone))
          }
          setSelectedTime={setSelectedTime}
          timeFormat={timeFormat}
          selectedTimezone={timezone || getDefaultTimeZone(time_zone)}
          setSelectedTimezone={setSelectedTimezone}
        />
      )}

      {/* Schedule details display - only show for existing reports */}
      {selectedReport?.id && selectedReport?.deliver_time && (
        <ScheduleDisplay
          scheduleDetail={renderScheduledDetail()}
          selectedReport={selectedReport}
        />
      )}
    </>
  );
};
